<template>
    <transition name="nebula-modal-transition" mode="out-in">
        <NebulaModal v-if="showApplyModal" @close-modal="toggleRequestApplyModal" size="s">
            <template v-slot:header>
                <h2>{{ $t('Apply to Request') }}</h2>
            </template>
            <template v-slot:content>
                <p>
                    {{
                        $t("You are about to apply to %(educatorName)s 's request. Are you sure you want to apply?", {
                            educatorName: data.educatorName,
                        })
                    }}
                </p>
            </template>
            <template v-slot:actionsRow>
                <NebulaButton
                    v-if="!applicationLoading"
                    @click="toggleRequestApplyModal"
                    type="flat"
                    :text="$t('No, Cancel')"
                    :data-click-type="`Confirm Apply to Request: ${roleDisplay}`"
                    data-click-name="No, Cancel"
                />
                <NebulaButton
                    :isDisabled="applicationLoading"
                    @click="applyToRequest"
                    :text="applicationLoading ? $t('Processing') : $t('Yes, proceed')"
                    :data-click-type="`Confirm Apply to Request: ${roleDisplay}`"
                    data-click-name="Yes, proceed"
                />
            </template>
        </NebulaModal>
        <NebulaModal v-else-if="showRescheduleModal" @close-modal="toggleRequestRescheduleModal" size="s">
            <template v-slot:header>
                <h2>{{ $t('Rescheduling Request') }}</h2>
            </template>
            <template v-slot:content>
                <p>
                    {{ rescheduleText }}
                </p>
            </template>
            <template v-slot:actionsRow>
                <NebulaButton
                    v-if="!loadingRequestSubmit"
                    @click="toggleRequestRescheduleModal"
                    type="flat"
                    :text="$t('No, Cancel')"
                    :data-click-type="`Confirm Reschedule: ${roleDisplay}`"
                    data-click-name="No, Cancel"
                />
                <NebulaButton
                    :isDisabled="loadingRequestSubmit"
                    @click="submitRequest"
                    :text="loadingRequestSubmit ? $t('Processing') : $t('Yes, Save & Reschedule')"
                    :data-click-type="`Confirm Reschedule: ${roleDisplay}`"
                    data-click-name="Yes, Save & Reschedule"
                />
            </template>
        </NebulaModal>
        <NebulaModal v-else-if="showEditModal" @close-modal="toggleRequestEditModal" size="s">
            <template v-slot:header>
                <h2>{{ $t('Save Edits') }}</h2>
            </template>
            <template v-slot:content>
                <p>{{ $t('You have made edits to your original request. Are you sure you want to save?') }}</p>
            </template>
            <template v-slot:actionsRow>
                <NebulaButton
                    v-if="!loadingRequestSubmit"
                    @click="toggleRequestEditModal"
                    type="flat"
                    :text="$t('No, Cancel')"
                    :data-click-type="`Confirm Request Edits: ${roleDisplay}`"
                    data-click-name="No, Cancel"
                />
                <NebulaButton
                    :isDisabled="loadingRequestSubmit"
                    @click="submitEditedRequest"
                    :text="loadingRequestSubmit ? $t('Processing') : $t('Yes, Save Edits')"
                    :data-click-type="`Confirm Request Edits: ${roleDisplay}`"
                    data-click-name="Yes, Save Edits"
                />
            </template>
        </NebulaModal>
        <NebulaModal v-else-if="showCancelModal" @close-modal="toggleRequestCancelModal" size="s">
            <template v-slot:header>
                <h2>{{ $t('Cancel Request') }}</h2>
            </template>
            <template v-slot:content>
                <p>{{ $t('You are about to cancel this request. Are you sure you want to cancel?') }}</p>
                <ProfileFormField
                    v-if="cancelScheduled"
                    name="cancellationMessage"
                    inputType="textarea"
                    :inputGroupData="formData.cancellationMessage"
                    :validation="validateInput"
                />
            </template>
            <template v-slot:actionsRow>
                <NebulaButton
                    v-if="!loadingRequestSubmit"
                    @click="toggleRequestCancelModal"
                    type="flat"
                    :text="$t('No, Don\'t Cancel')"
                    :data-click-type="`Confirm Cancel Request: ${roleDisplay}`"
                    data-click-name="No, Don't Cancel"
                />
                <NebulaButton
                    :isDisabled="loadingRequestSubmit"
                    @click="cancelRequest"
                    :text="loadingRequestSubmit ? $t('Processing') : $t('Yes, Cancel')"
                    :data-click-type="`Confirm Cancel Request: ${roleDisplay}`"
                    data-click-name="Yes, Cancel"
                />
            </template>
        </NebulaModal>
        <NebulaModal v-else-if="showApproveModal" @close-modal="toggleModal('approve', 'requestApproveModal')" size="s">
            <template v-slot:header>
                <h2>{{ $t('Approve an Applicant') }}</h2>
            </template>
            <template v-slot:content>
                <p>{{ $t('You are about to approve an applicant for this request. Are you sure you want to approve this applicant?') }}</p>
                <WithinNextN :timestamp="modalStore.requestApproveModal.startDateTime">
                    <ModalWarning>
                        <p>
                            {{
                                $t('This request is scheduled within the next 24 hours. This volunteer may not be available at this scheduled time.')
                            }}
                        </p>
                        <p>
                            {{ $t('If you would like to change the proposed time to further in the future, you may') }}
                            <RouterLink
                                class="request-modal__link"
                                @click="toggleModal('approve', 'requestApproveModal')"
                                :to="`/request/${modalStore.requestApproveModal.requestId}/edit`"
                            >
                                {{ $t('edit the request.') }}
                            </RouterLink>
                        </p>
                    </ModalWarning>
                </WithinNextN>
            </template>
            <template v-slot:actionsRow>
                <NebulaButton
                    v-if="!applicationLoading"
                    @click="toggleModal('approve', 'requestApproveModal')"
                    type="flat"
                    :text="$t('Cancel')"
                    :data-click-type="`Confirm Approve Applicant: ${roleDisplay}`"
                    data-click-name="Cancel"
                />
                <NebulaButton
                    :isDisabled="applicationLoading"
                    @click="approveApplicant"
                    :text="applicationLoading ? $t('Processing') : $t('Yes, Approve')"
                    :data-click-type="`Confirm Approve Applicant: ${roleDisplay}`"
                    data-click-name="Yes, Approve"
                />
            </template>
        </NebulaModal>
        <NebulaModal v-else-if="showDeclineModal" @close-modal="toggleModal('decline', 'requestDeclineModal')" size="s">
            <template v-slot:header>
                <h2>{{ $t('Decline an Applicant') }}</h2>
            </template>
            <template v-slot:content>
                <p>
                    {{
                        $t(
                            'You are about to decline an applicant. Once an applicant is declined, they will no longer be in your applicant list. Are you sure you want to continue?',
                        )
                    }}
                </p>
                <ProfileFormField
                    name="declineReason"
                    :inputGroupData="formData.declineReason"
                    :validation="validateInput"
                    :selectOptions="applicantDeclineReasons"
                />
            </template>
            <template v-slot:actionsRow>
                <NebulaButton
                    v-if="!applicationLoading"
                    @click="toggleModal('decline', 'requestDeclineModal')"
                    type="flat"
                    :text="$t('No, Keep Applicant')"
                    :data-click-type="`Confirm Decline Applicant: ${roleDisplay}`"
                    data-click-name="No, Keep Applicant"
                />
                <NebulaButton
                    :isDisabled="applicationLoading || declineReasonMissing"
                    @click="declineApplicant"
                    :text="applicationLoading ? $t('Processing') : $t('Yes, Decline')"
                    :data-click-type="`Confirm Decline Applicant: ${roleDisplay}`"
                    data-click-name="Yes, Decline"
                />
            </template>
        </NebulaModal>
        <NebulaModal v-else-if="showDeclineDirectModal" @close-modal="toggleModal('decline-direct', 'requestDeclineDirectModal')" size="s">
            <template v-slot:header>
                <h2>{{ $t('Decline Request') }}</h2>
            </template>
            <template v-slot:content>
                <p>{{ $t('Are you sure you want to decline this request?') }}</p>
            </template>
            <template v-slot:actionsRow>
                <NebulaButton
                    v-if="!loadingDecline"
                    @click="toggleModal('decline-direct', 'requestDeclineDirectModal')"
                    type="flat"
                    :text="$t('No, Cancel')"
                    :data-click-type="`Confirm Decline Direct Request: ${roleDisplay}`"
                    data-click-name="No, Cancel"
                />
                <NebulaButton
                    class="request-modal__danger-button"
                    :isDisabled="loadingDecline"
                    @click="declineDirectRequest"
                    :text="loadingDecline ? $t('Processing') : $t('Yes, Decline')"
                    :data-click-type="`Confirm Decline Direct Request: ${roleDisplay}`"
                    data-click-name="Yes, Decline"
                />
            </template>
        </NebulaModal>
    </transition>
</template>

<script>
import { NebulaModal, NebulaButton } from '@discoveryedu/nebula-components';
import { useMainStore } from '@/stores/useMainStore';
import { useModalStore } from '@/stores/useModalStore';
import { useRequestStore } from '@/stores/useRequestStore';
import { useMessagingStore } from '@/stores/useMessagingStore';
import { useEmployeeStore } from '@/stores/useEmployeeStore';
import createPayload from '@/mixins/data/createPayload';
import ModalWarning from '@/components/modals/shared/ModalWarning.vue';
import WithinNextN from '@/components/shared/utilities/WithinNextN.vue';
import ProfileFormField from '@/components/shared/Profile/ProfileFormField.vue';
import { scheduled, pendingMatches, roles, ROLE_DISPLAY_MAP } from '@/constants';
import validation from '@/mixins/form/validation';
import { applicantDeclineReasons } from '@/data/formData';
import { useAppStore } from '@/stores/useAppStore';
import { useAttachmentStore } from '@/stores/useAttachmentStore';
import { useErrorReporting } from '@/composables/useErrorReporting';
import { getRequestUpdateError } from '@/utils/errorMessages';

export default {
    setup() {
        const store = useMainStore();
        const modalStore = useModalStore();
        const appStore = useAppStore();
        const requestStore = useRequestStore();
        const attachmentStore = useAttachmentStore();
        const messagingStore = useMessagingStore();
        const employeeStore = useEmployeeStore();
        const { reportNonFatalError } = useErrorReporting();
        return {
            store,
            modalStore,
            appStore,
            requestStore,
            attachmentStore,
            messagingStore,
            employeeStore,
            reportNonFatalError,
        };
    },
    name: 'UserManagementModals',
    components: {
        NebulaModal,
        NebulaButton,
        ModalWarning,
        WithinNextN,
        ProfileFormField,
    },
    mixins: [createPayload, validation],
    computed: {
        applicantDeclineReasons,
        showApplyModal() {
            return this.modalStore.requestApplyModal.show;
        },
        showRescheduleModal() {
            return this.modalStore.requestRescheduleModal.show;
        },
        showEditModal() {
            return this.modalStore.requestEditModal.show;
        },
        showCancelModal() {
            return this.modalStore.requestCancelModal.show;
        },
        showApproveModal() {
            return this.modalStore.requestApproveModal.show;
        },
        showDeclineModal() {
            return this.modalStore.requestDeclineModal.show;
        },
        showDeclineDirectModal() {
            return this.modalStore.requestDeclineDirectModal.show;
        },
        data() {
            return this.modalStore.requestApplyModal;
        },
        cancelScheduled() {
            return this.modalStore.requestCancelModal.requestStatus === scheduled;
        },
        appMode() {
            return this.appStore.mode;
        },
        roleDisplay() {
            return ROLE_DISPLAY_MAP[this.appMode.trim().toLowerCase()];
        },
        applicationLoading() {
            return this.store.status.request_apply.loading;
        },
        loadingRequestSubmit() {
            return this.store.status.request_submit.loading;
        },
        loadingDecline() {
            return this.store.status.request_direct_decline.loading;
        },
        declineReasonMissing() {
            return !this.formData.declineReason.value;
        },
    },
    data() {
        const role = this.appStore.mode === roles.EMPLOYEE_ROLE ? this.$t('owner') : this.$t('participant');
        return {
            rescheduleText: this.$t('Are you sure you want to reschedule this request?'),
            category: 'request',
            formData: {
                cancellationMessage: {
                    value: '',
                    display: this.$t('Send a message to the request %(role)s', { role }),
                    required: false,
                    error: null,
                    status: '',
                    maxLength: 250,
                    placeholder: this.$t('Write a message'),
                },
                declineReason: {
                    value: '',
                    display: this.$t('Select a reason for declining the applicant'),
                    required: true,
                    error: null,
                    status: '',
                    helperText: this.$t('This feedback will be sent to the applicant'),
                },
            },
        };
    },
    methods: {
        toggleRequestApplyModal() {
            this.modalStore.toggleModal({
                category: 'request',
                type: 'apply',
                show: !this.modalStore.requestApplyModal.show,
            });
        },
        toggleRequestRescheduleModal() {
            this.modalStore.toggleModal({
                category: 'request',
                type: 'reschedule',
                show: !this.modalStore.requestRescheduleModal.show,
            });
        },
        toggleRequestEditModal() {
            this.modalStore.toggleModal({
                category: 'request',
                type: 'edit',
                show: !this.modalStore.requestEditModal.show,
            });
        },
        toggleRequestCancelModal() {
            this.modalStore.toggleModal({
                category: 'request',
                type: 'cancel',
                show: !this.modalStore.requestCancelModal.show,
            });
        },
        toggleRequestDeclineDirectModal() {
            this.modalStore.toggleModal({
                category: 'request',
                type: 'decline-direct',
                show: !this.modalStore.requestDeclineDirectModal.show,
            });
        },
        toggleModal(type, getter) {
            this.modalStore.toggleModal({
                category: this.category,
                type,
                show: !this.modalStore[getter].show,
            });
        },
        async applyToRequest() {
            this.store.updateLoading({ key: 'request_apply', status: true });
            const {
                avatarUrl: employeeAvatarUrl,
                firstName: employeeFirstName,
                lastName: employeeLastName,
                employeeId,
                preferredName: employeePreferredName,
            } = this.employeeStore.user.data;

            const { requestId } = this.data;

            const myData = {
                employeeAvatarUrl,
                employeeFirstName,
                employeeLastName,
                employeeId,
                employeePreferredName,
                operation: 'apply',
                requestId,
            };
            const payload = await this.createPayload(myData);

            this.requestStore.updateRequest(payload);

            const reFetchPayload = await this.createPayload({ requestId });
            await this.requestStore.getRequests(reFetchPayload);

            this.store.updateLoading({ key: 'request_apply', status: false });
            this.store.updateResults({ key: 'request_apply', status: 'success' });

            this.modalStore.toggleModal({
                category: 'request',
                type: 'apply',
                show: false,
            });
        },
        routeToRequestDetails(request) {
            const { requestId } = request;
            this.$router.push(`/request/${requestId}`);
        },
        async submitRequest() {
            const { onConfirm } = this.modalStore.requestRescheduleModal;
            if (!onConfirm) return;

            this.toggleRequestRescheduleModal();
            // useRequestForm composable handles business logic
            await onConfirm?.();
        },
        async submitEditedRequest() {
            const { onConfirm } = this.modalStore.requestEditModal;
            if (!onConfirm) return;

            this.toggleRequestEditModal();
            // useRequestForm composable handles business logic
            await onConfirm?.();
        },
        async cancelRequest() {
            this.store.updateLoading({ key: 'request_submit', status: true });

            const { requestId } = this.modalStore.requestCancelModal;
            const payload = await this.createPayload({ requestId, cancelReason: this.formData.cancellationMessage.value });
            await this.requestStore.cancelRequest(payload);
            this.store.updateLoading({ key: 'request_submit', status: false });

            this.toggleRequestCancelModal();
            this.$router.push('/');
        },
        async declineDirectRequest() {
            this.store.updateLoading({ key: 'request_direct_decline', status: true });

            try {
                const { requestId } = this.modalStore.requestDeclineDirectModal;
                const payload = await this.createPayload({ requestId });
                await this.requestStore.declineDirectRequest(payload);
                this.store.updateLoading({ key: 'request_direct_decline', status: false });

                this.toggleRequestDeclineDirectModal();
                this.$router.push('/');
            } catch (error) {
                this.store.updateLoading({ key: 'request_direct_decline', status: false });
                this.reportNonFatalError(error, getRequestUpdateError());
            }
        },
        async approveApplicant() {
            this.store.updateLoading({ key: 'request_apply', status: true });

            const { requestId, employeeId } = this.modalStore.requestApproveModal;

            const payload = await this.createPayload({ employeeId, requestId });
            await this.requestStore.acceptApplicant(payload);

            const statusPayload = await this.createPayload({ status: scheduled, requestId });
            await this.requestStore.updateRequest(statusPayload);

            if (this.requestStore.request.status === scheduled) {
                await this.store.updateResults({ key: 'application_accept', status: 'success' });
            }
            // enable messaging after a request transitions to scheduled
            // const messagePayload = await this.createPayload({ requestId });
            // this.messagingStore.createMessageStore(messagePayload);

            this.store.updateLoading({ key: 'request_apply', status: false });

            this.toggleModal('approve', 'requestApproveModal');
        },
        async declineApplicant() {
            if (this.declineReasonMissing) return;
            this.store.updateLoading({ key: 'request_apply', status: true });

            try {
                const { requestId, employeeId } = this.modalStore.requestDeclineModal;
                const {
                    declineReason: { value: declineReason },
                } = this.formData;

                const payload = await this.createPayload({ employeeId, requestId, decline_reason: declineReason });

                await this.requestStore.declineApplicant(payload);

                const applicantsPayload = await this.createPayload({ requestId });
                await this.requestStore.getApplicants(applicantsPayload);

                // Guard against page.applicants being null/undefined if the API returned unexpected data
                const hasActiveApplicant = !!(this.store.page.applicants || []).find((applicant) => !applicant.declined);

                // if there are no active, not-declined applicants, update the status back to pending matches
                if (!hasActiveApplicant) {
                    const statusPayload = await this.createPayload({ status: pendingMatches, requestId });
                    await this.requestStore.updateRequest(statusPayload);

                    // Re-fetch the single request to reflect the updated status in the view
                    const refreshPayload = await this.createPayload({ requestId });
                    await this.requestStore.getRequests(refreshPayload);
                }

                // Reset the decline reason so it does not pre-populate for the next decline
                this.formData.declineReason.value = '';
            } catch (error) {
                this.reportNonFatalError(error, getRequestUpdateError());
            }

            this.store.updateLoading({ key: 'request_apply', status: false });

            this.toggleModal('decline', 'requestDeclineModal');
        },
    },
    watch: {
        showRescheduleModal(show) {
            if (show) {
                const { hasConnector } = this.modalStore.requestRescheduleModal;
                if (hasConnector) {
                    this.rescheduleText = this.$t(
                        'Are you sure you want to reschedule this request? If so, the employee assigned to this request will need to re-confirm their availability prior to re-accepting this updated request.',
                    );
                }
            }
        },
    },
};
</script>

<style lang="stylus">
.request-modal {
    &__danger-button {
        --nebula-button-background: var(----nebula-color-feedback-error-600);
        --nebula-button-background-hover: var(----nebula-color-feedback-error-700);
        --nebula-button-background-active: var(----nebula-color-feedback-error-800);
    }
    &__link {
        link();
    }
}
</style>
